import { FocusEvent, KeyboardEvent, ReactNode, useLayoutEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

import { palettes, tokens } from '../../theme/tokens';

type FieldProps = {
	disabled?: boolean;
	error?: string | boolean;
	valid?: boolean;
	$filled?: boolean;
	$hasAction?: boolean;
};

const Field = styled.textarea<FieldProps>`
	border: 1px solid ${tokens.border.input};
	box-sizing: border-box;
	height: auto;
	min-height: 101px;
	padding: ${tokens.padding.compact}px ${tokens.padding.control}px;

	${(props) =>
		props.$hasAction &&
		css`
			padding-right: ${tokens.padding.control + 32 + tokens.gap.inline}px;
			padding-bottom: ${tokens.padding.compact + 32 + tokens.gap.inline}px;
		`}
	font-size: ${tokens.typography.sizes.label}px;
	font-weight: 500;
	line-height: 1.4;
	font-family: ${tokens.typography.fontFamily};
	width: 100%;
	-webkit-appearance: none;
	appearance: none;
	background: ${tokens.surface.white};
	color: ${tokens.text.primary};
	border-radius: ${tokens.radius.sm}px;
	box-shadow: ${tokens.shadow.input};
	cursor: text;
	resize: none;
	outline: none;
	transition:
		border-color 0.1s ease-out,
		background 0.1s ease-out;

	&::placeholder {
		color: ${tokens.text.tertiary};
		font-weight: 500;
	}

	${(props) =>
		!props.disabled &&
		css`
			&:hover,
			&[data-preview='hover'] {
				border-color: ${tokens.border.strong};
			}
		`}

	${(props) =>
		!props.valid &&
		!props.error &&
		css`
			&[placeholder]:not(:placeholder-shown):not(:focus):not(:hover):not(:disabled):not(
					[data-preview]
				) {
				border-color: ${tokens.border.subtle};
			}
		`}

	${(props) =>
		props.$filled &&
		!props.valid &&
		!props.error &&
		css`
			&:not(:focus):not(:hover):not(:disabled):not([data-preview]) {
				border-color: ${tokens.border.subtle};
			}
		`}

	&:focus,
	&[data-preview='focus'] {
		outline: none;
		border-color: ${tokens.border.focus};
	}

	${(props) =>
		props.valid &&
		css`
			border-color: ${tokens.success.border};

			&:hover:not(:disabled),
			&:focus,
			&[data-preview],
			&[placeholder]:not(:placeholder-shown):not(:focus):not(:hover):not(:disabled) {
				border-color: ${tokens.success.border};
			}
		`}

	${(props) =>
		props.error &&
		css`
			border-color: ${tokens.error.border};

			&:hover:not(:disabled),
			&:focus,
			&[data-preview],
			&[placeholder]:not(:placeholder-shown):not(:focus):not(:hover):not(:disabled) {
				border-color: ${tokens.error.border};
			}
		`}

	${(props) =>
		props.disabled &&
		css`
			background: ${palettes.heatherGray['100']};
			border-color: ${tokens.border.subtle};
			color: ${tokens.text.tertiary};

			&:hover,
			&[data-preview='hover'] {
				border-color: ${tokens.border.subtle};
			}

			&::placeholder {
				color: ${tokens.text.tertiary};
			}
		`}
`;

const Wrapper = styled.div`
	position: relative;
	width: 100%;
`;

const Action = styled.div`
	position: absolute;
	right: ${tokens.gap.element}px;
	bottom: ${tokens.gap.element}px;
`;

export function TextArea(props: {
	autoFocus?: boolean;
	minRows?: number;
	maxRows?: number;
	placeholder?: string;
	value?: string;
	disabled?: boolean;
	error?: string | boolean;
	valid?: boolean;
	action?: ReactNode;
	onChange?: (text: string) => void;
	onFocus?: (event: FocusEvent<HTMLTextAreaElement>) => void;
	onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
	onBlur?: () => void;
	'data-preview'?: 'hover' | 'focus';
}) {
	const ref = useRef<HTMLTextAreaElement>(null);

	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) return;
		el.style.height = 'auto';
		el.style.height = `${el.scrollHeight}px`;
	}, [props.value, props.minRows, props.maxRows]);

	const field = (
		<Field
			ref={ref}
			$filled={Boolean(props.value)}
			$hasAction={Boolean(props.action)}
			disabled={props.disabled}
			error={props.error}
			valid={props.valid}
			placeholder={props.placeholder}
			value={props.value ?? ''}
			autoFocus={props.autoFocus}
			data-preview={props['data-preview']}
			onChange={(event) => props.onChange?.(event.currentTarget.value)}
			onFocus={props.onFocus}
			onKeyDown={props.onKeyDown}
			onBlur={props.onBlur}
			style={{
				overflowY: 'auto',
				minHeight: props.minRows === undefined ? undefined : `${props.minRows * 20}px`,
				maxHeight: props.maxRows === undefined ? undefined : `${props.maxRows * 20}px`,
			}}
		/>
	);

	if (!props.action) {
		return field;
	}

	return (
		<Wrapper>
			{field}
			<Action>{props.action}</Action>
		</Wrapper>
	);
}
